import { useEffect, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { 
  Search, 
  Loader2,
  CreditCard,
  ExternalLink
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [voucherLoadingId, setVoucherLoadingId] = useState('');
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    fetchData();
  }, [search, staffFilter, methodFilter, sourceFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (staffFilter !== 'all') params.sales_staff_id = staffFilter;
      if (methodFilter !== 'all') params.payment_method = methodFilter;
      if (sourceFilter !== 'all') params.collection_source = sourceFilter;
      
      const [paymentsRes, staffRes] = await Promise.all([
        adminApi.getPayments(params),
        adminApi.getSalesStaff()
      ]);
      
      setPayments(paymentsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0) + (p.credit_note_amount || 0), 0);

  const handleViewVoucherPdf = async (payment) => {
    if (!payment?.voucher_id) {
      toast.error('No voucher linked to this payment');
      return;
    }
    setVoucherLoadingId(payment.id);
    try {
      const res = await adminApi.getVoucherPdfUrl(payment.voucher_id);
      const url = res?.data?.voucher_pdf_url;
      if (!url) {
        throw new Error('Voucher PDF URL unavailable');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to open voucher PDF');
    } finally {
      setVoucherLoadingId('');
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('payments-list', {
        search: search || undefined,
        staff_id: staffFilter !== 'all' ? staffFilter : undefined,
        payment_mode: methodFilter !== 'all' ? methodFilter : undefined,
        collection_source: sourceFilter !== 'all' ? sourceFilter : undefined,
      });
      downloadBlob(res.data, 'payments.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Payments</h1>
          <p className="text-[#A8A8A8] mt-1">View all payment collections</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleDownloadExcel}>
            Download Excel
          </Button>
          <div className="p-4 bg-white rounded-xl border border-[#E5E5E5]">
            <p className="text-xs text-[#A8A8A8]">Total Shown</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{formatCurrency(totalAmount)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A8A8]" />
              <Input
                placeholder="Search by customer name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="search-input"
              />
            </div>
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]" data-testid="staff-filter">
                <SelectValue placeholder="Sales Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-full lg:w-40 border-[#E5E5E5]" data-testid="method-filter">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]">
                <SelectValue placeholder="Collection Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="direct_company">Received by Company</SelectItem>
                <SelectItem value="staff_collected">Collected by Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <CreditCard className="w-12 h-12 mb-4" />
              <p>No payments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Date</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Voucher</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Customer</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Order Ref</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Sales Staff</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Amount</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Credit Note</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Source</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Method</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {payments.map((p) => (
                    <tr key={p.id} className="hover:bg-[#FAFAF9]/50 transition-colors" data-testid={`payment-row-${p.id}`}>
                      <td className="py-4 px-6 text-[#A8A8A8]">{formatDate(p.created_at)}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{p.voucher_no || '-'}</td>
                      <td className="py-4 px-6 font-medium text-[#2D2420]">{p.customer_name}</td>
                      <td className="py-4 px-6 text-[#A8A8A8]">{p.order_id || '-'}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{p.sales_staff_name}</td>
                      <td className="py-4 px-6 font-semibold text-[#4A7c59]">{formatCurrency(p.amount)}</td>
                      <td className="py-4 px-6 text-[#2D2420]">
                        {p.credit_note_no ? `${p.credit_note_no} • ${formatCurrency(p.credit_note_amount)}` : '-'}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className="bg-[#F3E8E2] text-[#5C4033]">
                          {p.collection_source === 'staff_collected' ? 'staff collected' : 'company'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">
                        {p.collection_source === 'staff_collected'
                          ? `${p.collection_status || 'pending_handover'}${Number(p.remaining_with_staff || 0) > 0 ? ` • ${formatCurrency(p.remaining_with_staff)}` : ''}`
                          : 'settled'}
                      </td>
                      <td className="py-4 px-6">
                        <Badge className="bg-[#D2B48C]/20 text-[#5C4033]">
                          {p.credit_note_no ? 'credit note' : (p.customer_paid_mode || p.payment_method)}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewVoucherPdf(p)}
                          disabled={!p.voucher_id || voucherLoadingId === p.id}
                          className="border-[#E5E5E5]"
                        >
                          {voucherLoadingId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
